/**
 * OosStatusPopover: the Order Queue Status cell for an order blocked by stock.
 *
 * The OOS badge is the trigger. Hovering it with a mouse, focusing it from the
 * keyboard, or tapping it shows how many SKUs are out of stock and, for roles
 * that can open the Issues page, a link to this order in the OOS issues list.
 * The pointer can cross onto the popover to reach the link; from the keyboard,
 * Enter moves focus to it and Escape returns to the badge. The count is also
 * the badge's screen-reader description, so hover is never the only way to
 * get it. Clicks never expand the row.
 */
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ProductionStatusBadge } from '../ProductionStatusBadge';
import { useRowPopover } from './useRowPopover';

export interface OosStatusPopoverProps {
  /** The order name as stored (with its #); seeds the OOS issues search. */
  orderName: string | null;
  /** Distinct out-of-stock SKUs, counted by the server. */
  skuCount: number;
  /** Caller may open /production/issues (canOpenIssuesPage); else count only. */
  canOpenIssues: boolean;
}

export function OosStatusPopover({ orderName, skuCount, canOpenIssues }: OosStatusPopoverProps) {
  const { t } = useTranslation('production');
  const { rootProps, triggerProps, contentProps, focusTargetRef } = useRowPopover({ interactive: true });
  const countId = useId();
  const descriptionId = useId();
  const countLabel = t('orders.oos.skuCount', { count: skuCount });
  const issuesQuery = new URLSearchParams(orderName ? { tab: 'oos', search: orderName } : { tab: 'oos' });
  const issuesHref = `/production/issues?${issuesQuery.toString()}`;

  return (
    <Popover {...rootProps}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-describedby={descriptionId}
          // before: widens the hit area to 24px tall (DESIGN.md §16) without
          // growing the badge or the row.
          className="group/oos relative inline-flex min-w-0 max-w-full rounded-md before:absolute before:inset-x-0 before:top-1/2 before:h-6 before:-translate-y-1/2 before:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          {...triggerProps}
        >
          {/* No native title: it would pop over the popover's count line. */}
          <ProductionStatusBadge
            status="OOS"
            truncate
            title={null}
            className="group-hover/oos:bg-red-100 group-data-[state=open]/oos:bg-red-100 motion-reduce:transition-none"
          />
        </button>
      </PopoverTrigger>
      <span id={descriptionId} className="sr-only">
        {countLabel}
      </span>
      {/* Beside the badge, over this row's own cells: below it, it covers the
          next row's badge and a sweep down the Status column skips rows.
          Radix still flips it when there is no room. */}
      <PopoverContent
        {...contentProps}
        side="right"
        align="center"
        aria-labelledby={countId}
        // `!`: the primitive's data-[state=open]:animate-in outranks a plain
        // motion-reduce:animate-none (attribute selector, higher specificity).
        className="w-auto max-w-xs space-y-1 px-3 py-2 motion-reduce:!animate-none"
      >
        <p id={countId} className="whitespace-nowrap text-sm font-medium tabular-nums text-foreground">
          {countLabel}
        </p>
        {canOpenIssues ? (
          <Link
            ref={focusTargetRef}
            to={issuesHref}
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-sm text-sm font-medium text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('orders.oos.viewInIssues')}
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
